/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderUserContext,
  StageAdvancementNotificationService,
  toIsoDate,
} from '@gh/builder-shared';
import { CLOSED_LOST_STAGE_CODE, LeadBoardListItemParams, Priority, Stage } from '@gh/builder-shared-data';
import { EnumDictionary } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService, WaitingService } from '@gh/core-ui';

import { Observable, Subscription } from 'rxjs';
import { merge$ } from '@gh/rx';

import { LeadBoardBusinessService } from '../../resources/lead-board-business.service';
import {
  cardUpdateParams,
  isLeadBoardItemHasSameColumnId,
  LeadBoardColumnId,
  LeadBoardListItem,
  LeadBoardMobileFilter,
  LeadBoardScroll,
  LeadBoardScrollTarget,
  leadBoardMoveBlockedMessage,
  SalespersonSelectItem,
} from '../../resources/lead-board.model';
import { LeadBoardFormState } from './lead-board-form.service';
import { VirtualPosition } from './virtual-rendering';
import { isFunction } from 'lodash';
import { LostLeadReasonDialogResult } from './lead-board-container/lead-board-card/lost-lead-reason-dialog/lost-lead-reason-dialog.component';
import { createLostLeadReasonDialog } from './lead-board-container/lead-board-card/lost-lead-reason-dialog/lost-lead-reason-dialog.route';

@Injectable()
export class LeadBoardCommandService implements OnDestroy {

  private readonly stageUndoneSubscription: Subscription;

  constructor(private route: ActivatedRoute,
              private businessService: LeadBoardBusinessService,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private userContext: BuilderUserContext,
              private form: LeadBoardFormState,
              private dialogService: DialogService,
              private stageAdvancement: StageAdvancementNotificationService) {
    this.stageUndoneSubscription = merge$(
      this.stageAdvancement.stageUndone$,
      this.stageAdvancement.stageAdvanced$,
    ).subscribe(() => {
      this.filterLeadBoardCardsByInfo();
    });
  }

  ngOnDestroy(): void {
    this.stageUndoneSubscription.unsubscribe();
  }

  filterLeadBoardCardsByInfo(callback?: any): void {
    const { salesPersonId, showCompleted, showActive, showInactive, showOverdue, showSuppressed, searchString } = this.form.value.info;
    this.businessService.findAll(salesPersonId, { showCompleted, showActive, showInactive, showOverdue, showSuppressed, searchString }).pipe(
      this.waitingService.operator())
      .subscribe((list: LeadBoardListItem[]) => {
        this.form.refreshBoardList(list);
        this.form.resetScroll();
        if (callback && isFunction(callback)) {
          callback();
        }
      });
  }

  buildForm(salesPersonId: Maybe<ID>,
            salespersonList: SalespersonSelectItem[],
            list: LeadBoardListItem[],
            priorities: EnumDictionary<Priority>,
            stages: EnumDictionary<Stage>,
            isEditMode: boolean,
            leadBordFilter: any): void {
    this.form.build(salesPersonId, salespersonList, list, priorities, stages, isEditMode, leadBordFilter);
  }

  setSalespersonId(id: Maybe<ID>): void {
    this.form.setSalespersonId(id);
  }

  setSearchString(value: Maybe<string>): void {
    this.form.setSearchString(value);
  }

  setInfoShowCompletedFilter(isVisible: boolean): void {
    this.form.setInfoShowCompletedFilter(isVisible);
  }

  setInfoShowActiveFilter(isVisible: boolean): void {
    this.form.setInfoShowActiveFilter(isVisible);
  }

  setInfoShowInactiveFilter(isVisible: boolean): void {
    this.form.setInfoShowInactiveFilter(isVisible);
  }

  setInfoShowOverdueFilter(isVisible: boolean): void {
    this.form.setInfoShowOverdueFilter(isVisible);
  }

  startDrag(card: LeadBoardListItem): void {
    this.form.setItemAsDragged(card, true);
  }

  stopDrag(card: LeadBoardListItem): void {
    this.form.setItemAsDragged(card, false);
  }

  enterZone(columnId: LeadBoardColumnId, card: LeadBoardListItem): void {
    this.form.enterZone(columnId, card);
  }

  exitZone(columnId: LeadBoardColumnId, card: LeadBoardListItem): void {
    this.form.exitZone(columnId, card);
  }

  dropItem(card: LeadBoardListItem, columnId: LeadBoardColumnId): void {
    if (isLeadBoardItemHasSameColumnId(card, columnId)) {
      return;
    }

    const blockedMessage = leadBoardMoveBlockedMessage(card, columnId);
    if (blockedMessage) {
      this.notificationService.warning(blockedMessage);
      return;
    }

    if (columnId.stage === CLOSED_LOST_STAGE_CODE) {
      this.dialogService.openWithInput(createLostLeadReasonDialog, { card })
        .afterClosed()
        .subscribe((payload: LostLeadReasonDialogResult) => {
          if (!payload || !payload.notProceedingReason) {
            return;
          }
          const reason = Number(payload.notProceedingReason);
          this.updateCard(card.salesFolderId, {
            ...cardUpdateParams(card),
            ...columnId,
            notProceedingReason: reason,
            notProceedingReasonOther: payload.notProceedingReasonOther,
            reEngageDate: payload.reEngageDate ? toIsoDate(payload.reEngageDate) : undefined,
          }).subscribe(() => {
            this.filterLeadBoardCardsByInfo();
          }, this.notificationService.processAll);
        });
      return;
    }

    this.updateCard(card.salesFolderId, {
      ...cardUpdateParams(card),
      ...columnId,
    }).subscribe(({ version }: LeadBoardListItemParams) => {
      this.moveCard(card, columnId);
      this.form.refreshCardVersion(card, version);
    }, this.notificationService.processAll);
  }

  updateFollowUpDate(card: LeadBoardListItem, followUpDate: Date): void {
    this.updateCard(card.salesFolderId, {
      ...cardUpdateParams(card),
      followUpDate,
    }).subscribe(({ version }: LeadBoardListItemParams) => {
      this.form.refreshCardVersion(card, version);
      this.form.setSalesFolderFollowUpDate(card, followUpDate);
    }, this.notificationService.processAll);
  }

  incrementCardActivitiesCount(card: LeadBoardListItem): void {
    this.form.incrementCardActivitiesCount(card);
  }

  moveCard(card: LeadBoardListItem, moveParams: LeadBoardColumnId): void {
    this.form.moveCard(card, moveParams);
  }

  setMobileFilter(filter: LeadBoardMobileFilter): void {
    this.form.setMobileFilter(filter);
  }

  setMobilePriority(priority: number): void {
    this.form.setMobilePriority(priority);
    this.form.resetScroll();
  }

  setMobileStage(stage: number): void {
    this.form.setMobileStage(stage);
    this.form.resetScroll();
  }

  setFilter(filter: any): void {
    this.form.setFilter(filter);
  }

  setScroll(scroll: LeadBoardScroll): void {
    this.form.setScroll(scroll);
  }

  setScrollPosition(position: VirtualPosition): void {
    this.form.setScroll({ target: LeadBoardScrollTarget.Standard, position });
  }

  setMobileScrollPosition(position: VirtualPosition): void {
    this.form.setScroll({ target: LeadBoardScrollTarget.Mobile, position });
  }

  toggleCard(id: ID): void {
    this.form.toggleCard(id);
  }

  setStartFilter(filter: any): void {
    this.form.setStartFilter(filter);
  }

  private updateCard(salesFolderId: ID, params: LeadBoardListItemParams): Observable<LeadBoardListItemParams> {
    return this.businessService.update(salesFolderId, params).pipe(
      this.waitingService.operator());
  }
}
